// FUNCTIONALITY-115B-R5 — optional customer attachment for availability requests.
import type { StoreV2Lang } from "./store-v2-locale.js";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);

export interface AvailabilityAttachment {
  file: File;
  fileName: string;
  contentType: "image/jpeg" | "image/png" | "image/webp";
  sizeBytes: number;
  contentHash: string;
}

function magicType(bytes: Uint8Array): "image/jpeg" | "image/png" | "image/webp" | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return null;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function validateAvailabilityAttachment(
  file: File,
  lang: StoreV2Lang,
): Promise<{ ok: true; attachment: AvailabilityAttachment } | { ok: false; error: string }> {
  const fail = (ua: string, en: string) => ({
    ok: false as const,
    error: lang === "en" ? en : ua,
  });
  if (!file) return fail("Файл не вибрано.", "No file selected.");
  const name = (file.name || "photo").toLowerCase();
  if (name.endsWith(".svg") || name.endsWith(".svgz")) {
    return fail("SVG не підтримується.", "SVG is not supported.");
  }
  if (/\.(exe|js|html|htm|pdf|gif)$/i.test(name)) {
    return fail("Непідтримуваний формат файлу.", "Unsupported file format.");
  }
  if (file.size <= 0 || file.size > MAX_BYTES) {
    return fail("Максимум 5 МБ.", "Maximum size is 5 MB.");
  }
  if (!ALLOWED.has(file.type) && file.type !== "") {
    return fail("Лише JPEG, PNG або WebP.", "Only JPEG, PNG or WebP.");
  }
  const buffer = await file.arrayBuffer();
  const magic = magicType(new Uint8Array(buffer));
  if (!magic) {
    return fail("Файл не є дійсним зображенням.", "File is not a valid image.");
  }
  // Dimension guard via createImageBitmap when available.
  try {
    if (typeof createImageBitmap === "function") {
      const bmp = await createImageBitmap(new Blob([buffer], { type: magic }));
      const w = bmp.width;
      const h = bmp.height;
      bmp.close?.();
      if (w < 32 || h < 32 || w > 8000 || h > 8000) {
        return fail("Некоректний розмір зображення.", "Invalid image dimensions.");
      }
    }
  } catch {
    return fail("Не вдалося прочитати зображення.", "Could not read the image.");
  }
  const contentHash = await sha256Hex(buffer);
  return {
    ok: true,
    attachment: {
      file: new File([buffer], file.name || `availability.${magic.split("/")[1]}`, { type: magic }),
      fileName: (file.name || "availability.jpg").slice(0, 80),
      contentType: magic,
      sizeBytes: buffer.byteLength,
      contentHash,
    },
  };
}
